import {
  Calendar,
  Pill,
  FileText,
  Copy,
  File,
  PenLine,
  Syringe,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { ConsultationTimeline } from "./ConsultationTimeline";
import React, { useEffect } from "react";
import { useConsultationsState } from "@/lib/dashboard/consultationsData";
import { usePatientFormState } from "@/lib/dashboard/patientDashboardData";
import useTitanApp from "@/hooks/useTitanApp";
import moment from "moment";
import {
  nhs111TemplateId,
  nhsContraceptionTemplateId,
  nhsSinusitisTemplateId
} from "@/data/constantStrings";
import {
  setConsultationLoadError,
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import { accessTitanverseConsultationUpsert } from "@/data/constantStrings";

function ConsultationDetails() {
  const { setSelectedConsultation, selectedConsultation } =
    useConsultationsState();
  const { getActionDetails, handleArrowClick, formatSummaryText } =
    usePatientFormState();
  const { appState } = useTitanApp();
  const { darkMode } = appState || {};
  const hasConsultationPermission = checkUserPermissions(
    accessTitanverseConsultationUpsert,
  );
  let isDarkMode = darkMode;
  return (
    <>
      <Dialog
        open={!!selectedConsultation}
        onOpenChange={() => setSelectedConsultation(null)}
      >
        <DialogContent
          className={`max-w-2xl ${
            isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
          }`}
        >
          <DialogHeader>
            <DialogTitle
              className={`flex items-center gap-2 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              <Calendar
                className={`w-5 h-5 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              />
              Consultation Details -{" "}
              {selectedConsultation?.completedDate
                ? selectedConsultation.completedDate
                : selectedConsultation?.date}
            </DialogTitle>
          </DialogHeader>

          {selectedConsultation && (
            <div className="space-y-6 overflow-y-auto max-h-[70vh] pr-2 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
              {/* Consultation Info */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-700" : "bg-gray-50"
                } rounded-lg p-4`}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {selectedConsultation?.completedDate
                      ? selectedConsultation.completedDate
                      : selectedConsultation?.date}
                  </div>
                  <div
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {selectedConsultation?.completedTime
                      ? selectedConsultation.completedTime
                      : selectedConsultation?.time}
                  </div>
                  <div
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {selectedConsultation?.pharmacist}
                  </div>
                </div>
                <div
                  className={`text-sm leading-relaxed whitespace-pre-line ${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  {/* {formatSummaryText(
                    selectedConsultation?.templateId === nhs111TemplateId ||
                      selectedConsultation?.templateId ===
                        nhsContraceptionTemplateId ||
                      selectedConsultation?.templateId ===
                        nhsSinusitisTemplateId
                      ? selectedConsultation.shortSummary
                      : selectedConsultation.summary,
                  )} */}
                   { formatSummaryText(selectedConsultation.shortSummary == null || selectedConsultation.shortSummary.trim() == ""  ? selectedConsultation.summary : selectedConsultation.shortSummary) }
                </div>
              </div>
              {/* Actions Taken */}
              <div>
                <h4
                  className={`text-sm font-semibold mb-3 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Actions Taken
                </h4>
                <div className="space-y-3">
                  {selectedConsultation?.actions?.map(
                    (action: any, index: number) => {
                      const { type, details, vaccineData } =
                        getActionDetails(action);
                      return (
                        <div
                          key={index}
                          className={`flex items-start gap-3 p-3 border rounded-lg ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600"
                              : "bg-white border-gray-200"
                          }`}
                        >
                          {/* Icon */}
                          <div
                            className={`p-2 rounded-lg ${
                              type === "prescription"
                                ? "bg-blue-100 text-blue-700"
                                : type === "payment"
                                  ? "bg-green-100 text-green-700"
                                  : type === "vaccine"
                                    ? "bg-cyan-100 text-cyan-700"
                                    : type === "referral"
                                      ? "bg-orange-100 text-orange-700"
                                      : type === "appointment"
                                        ? "bg-amber-100 text-amber-700"
                                        : type === "signature"
                                          ? "bg-indigo-100 text-indigo-700"
                                          : type === "system"
                                            ? "bg-purple-100 text-purple-700"
                                            : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {type === "prescription" && (
                              <Pill className="w-4 h-4" />
                            )}
                            {type === "payment" && <Copy className="w-4 h-4" />}
                            {type === "vaccine" && (
                              <Syringe className="w-4 h-4" />
                            )}
                            {type === "vitals" && <Pill className="w-4 h-4" />}
                            {type === "appointment" && (
                              <Clock className="w-4 h-4" />
                            )}
                            {type === "referral" && (
                              <FileText className="w-4 h-4" />
                            )}
                            {type === "signature" && (
                              <PenLine className="w-4 h-4" />
                            )}
                            {type === "system" && <File className="w-4 h-4" />}
                          </div>

                          {/* Content */}
                          <div className="flex-1 space-y-1">
                            <div
                              className={`text-sm font-medium capitalize ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {type === "prescription" && "Prescription Issued"}
                              {type === "payment" && "Payment Recorded"}
                              {type === "vaccine" && "Vaccine Administered"}
                              {type === "referral" && "Referral Created"}
                              {type === "vitals" && "Vitals Created"}
                              {type === "appointment" && "Booking Created"}
                              {type === "signature" && "Patient Signature"}
                              {type === "system" && "Document"}
                            </div>

                            {/* Show details if prescription */}
                            {type === "prescription" && details.length > 0 && (
                              <ul className="space-y-1 text-xs">
                                {details.map((d, i) => (
                                  <li
                                    key={i}
                                    className={
                                      isDarkMode
                                        ? "text-gray-300"
                                        : "text-gray-700"
                                    }
                                  >
                                    • {d.value} — {d.dose} ({d.quantity})
                                  </li>
                                ))}
                              </ul>
                            )}

                            {/* Show details if referral */}
                            {type === "referral" && (
                              <ul className="space-y-1 text-xs">
                                <li
                                  className={
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-700"
                                  }
                                >
                                  • Referral: Referred to GP
                                </li>
                              </ul>
                            )}

                            {/* Show details if vitals */}
                            {type === "vitals" && details && (
                              <div className="mt-2">
                                <div
                                  className={`p-3 rounded-lg border-2 ${
                                    isDarkMode
                                      ? "border-blue-500 bg-gray-800"
                                      : "border-blue-500 bg-white"
                                  }`}
                                >
                                  <h2
                                    className={`text-sm font-bold ${
                                      isDarkMode
                                        ? "text-blue-400"
                                        : "text-blue-600"
                                    } border-b border-blue-500 pb-1 mb-3`}
                                  >
                                    Vitals Record
                                  </h2>

                                  {/* Check if details is an array or object */}
                                  {Array.isArray(details) ? (
                                    // ARRAY FORMAT (from your logs)
                                    <div className="overflow-x-auto">
                                      <table
                                        className={`w-full border-collapse border text-xs ${
                                          isDarkMode
                                            ? "border-blue-700 text-gray-200"
                                            : "border-blue-500 text-gray-900"
                                        }`}
                                      >
                                        <thead>
                                          <tr
                                            className={
                                              isDarkMode
                                                ? "bg-blue-900"
                                                : "bg-blue-50"
                                            }
                                          >
                                            <th
                                              className={`border p-1 text-left ${
                                                isDarkMode
                                                  ? "border-blue-700"
                                                  : "border-blue-500"
                                              }`}
                                            >
                                              Vital
                                            </th>
                                            <th
                                              className={`border p-1 text-left ${
                                                isDarkMode
                                                  ? "border-blue-700"
                                                  : "border-blue-500"
                                              }`}
                                            >
                                              Value
                                            </th>
                                            <th
                                              className={`border p-1 text-left ${
                                                isDarkMode
                                                  ? "border-blue-700"
                                                  : "border-blue-500"
                                              }`}
                                            >
                                              Unit
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {details
                                            .filter(
                                              (vital: any) =>
                                                ![
                                                  "recordedAt",
                                                  "vitalType",
                                                  "vitalTypeKey",
                                                  "serviceName",
                                                ].includes(vital.name),
                                            )
                                            .map(
                                              (vital: any, index: number) => {
                                                let displayName = vital.name;
                                                switch (vital.name) {
                                                  case "bmi":
                                                    displayName = "BMI";
                                                    break;
                                                  case "height":
                                                    displayName = "Height";
                                                    break;
                                                  case "weight":
                                                    displayName = "Weight";
                                                    break;
                                                  case "waist":
                                                    displayName = "Waist";
                                                    break;
                                                  case "hip":
                                                    displayName = "Hip";
                                                    break;
                                                  case "neck":
                                                    displayName = "Neck";
                                                    break;
                                                  case "bpSystolic":
                                                    displayName =
                                                      "BP (Systolic)";
                                                    break;
                                                  case "bpDiastolic":
                                                    displayName =
                                                      "BP (Diastolic)";
                                                    break;
                                                  case "temperature":
                                                    displayName = "Temperature";
                                                    break;
                                                  case "pulse":
                                                    displayName = "Pulse";
                                                    break;
                                                  case "respiratoryRate":
                                                    displayName =
                                                      "Respiratory Rate";
                                                    break;
                                                  case "oxygenSaturation":
                                                    displayName =
                                                      "O₂ Saturation";
                                                    break;
                                                  default:
                                                    displayName =
                                                      vital.name
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                      vital.name.slice(1);
                                                }

                                                return (
                                                  <tr
                                                    key={index}
                                                    className={
                                                      index % 2 === 0
                                                        ? isDarkMode
                                                          ? "bg-gray-700"
                                                          : "bg-gray-50"
                                                        : ""
                                                    }
                                                  >
                                                    <td
                                                      className={`border p-1 ${
                                                        isDarkMode
                                                          ? "border-blue-700"
                                                          : "border-blue-500"
                                                      }`}
                                                    >
                                                      {displayName}
                                                    </td>
                                                    <td
                                                      className={`border p-1 ${
                                                        isDarkMode
                                                          ? "border-blue-700"
                                                          : "border-blue-500"
                                                      }`}
                                                    >
                                                      {vital.value}
                                                    </td>
                                                    <td
                                                      className={`border p-1 ${
                                                        isDarkMode
                                                          ? "border-blue-700"
                                                          : "border-blue-500"
                                                      }`}
                                                    >
                                                      {vital.unit}
                                                    </td>
                                                  </tr>
                                                );
                                              },
                                            )}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className="overflow-x-auto">
                                      <table
                                        className={`w-full border-collapse border text-xs ${
                                          isDarkMode
                                            ? "border-blue-700 text-gray-200"
                                            : "border-blue-500 text-gray-900"
                                        }`}
                                      >
                                        <thead>
                                          <tr
                                            className={
                                              isDarkMode
                                                ? "bg-blue-900"
                                                : "bg-blue-50"
                                            }
                                          >
                                            <th
                                              className={`border p-1 text-left ${
                                                isDarkMode
                                                  ? "border-blue-700"
                                                  : "border-blue-500"
                                              }`}
                                            >
                                              Measurement
                                            </th>
                                            <th
                                              className={`border p-1 text-left ${
                                                isDarkMode
                                                  ? "border-blue-700"
                                                  : "border-blue-500"
                                              }`}
                                            >
                                              Value
                                            </th>
                                            <th
                                              className={`border p-1 text-left ${
                                                isDarkMode
                                                  ? "border-blue-700"
                                                  : "border-blue-500"
                                              }`}
                                            >
                                              Unit
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {details.Systolic !== undefined &&
                                            details.Diastolic !== undefined && (
                                              <tr
                                                className={
                                                  isDarkMode
                                                    ? "bg-gray-700"
                                                    : "bg-gray-50"
                                                }
                                              >
                                                <td
                                                  className={`border p-1 ${
                                                    isDarkMode
                                                      ? "border-blue-700"
                                                      : "border-blue-500"
                                                  }`}
                                                >
                                                  Blood Pressure
                                                </td>
                                                <td
                                                  className={`border p-1 ${
                                                    isDarkMode
                                                      ? "border-blue-700"
                                                      : "border-blue-500"
                                                  }`}
                                                >
                                                  {details.Systolic}/
                                                  {details.Diastolic}
                                                </td>
                                                <td
                                                  className={`border p-1 ${
                                                    isDarkMode
                                                      ? "border-blue-700"
                                                      : "border-blue-500"
                                                  }`}
                                                >
                                                  mmHg
                                                </td>
                                              </tr>
                                            )}

                                          {details.Waist !== undefined && (
                                            <tr
                                              className={
                                                isDarkMode
                                                  ? "bg-gray-700"
                                                  : "bg-gray-50"
                                              }
                                            >
                                              <td
                                                className={`border p-1 ${
                                                  isDarkMode
                                                    ? "border-blue-700"
                                                    : "border-blue-500"
                                                }`}
                                              >
                                                Waist
                                              </td>
                                              <td
                                                className={`border p-1 ${
                                                  isDarkMode
                                                    ? "border-blue-700"
                                                    : "border-blue-500"
                                                }`}
                                              >
                                                {details.Waist}
                                              </td>
                                              <td
                                                className={`border p-1 ${
                                                  isDarkMode
                                                    ? "border-blue-700"
                                                    : "border-blue-500"
                                                }`}
                                              >
                                                {details.Unit || "cm"}
                                              </td>
                                            </tr>
                                          )}

                                          {details.Hip !== undefined && (
                                            <tr
                                              className={
                                                index % 2 === 0
                                                  ? isDarkMode
                                                    ? "bg-gray-700"
                                                    : "bg-gray-50"
                                                  : ""
                                              }
                                            >
                                              <td
                                                className={`border p-1 ${
                                                  isDarkMode
                                                    ? "border-blue-700"
                                                    : "border-blue-500"
                                                }`}
                                              >
                                                Hip
                                              </td>
                                              <td
                                                className={`border p-1 ${
                                                  isDarkMode
                                                    ? "border-blue-700"
                                                    : "border-blue-500"
                                                }`}
                                              >
                                                {details.Hip}
                                              </td>
                                              <td
                                                className={`border p-1 ${
                                                  isDarkMode
                                                    ? "border-blue-700"
                                                    : "border-blue-500"
                                                }`}
                                              >
                                                {details.Unit || "cm"}
                                              </td>
                                            </tr>
                                          )}

                                          {details.Neck !== undefined && (
                                            <tr
                                              className={
                                                isDarkMode
                                                  ? "bg-gray-700"
                                                  : "bg-gray-50"
                                              }
                                            >
                                              <td
                                                className={`border p-1 ${
                                                  isDarkMode
                                                    ? "border-blue-700"
                                                    : "border-blue-500"
                                                }`}
                                              >
                                                Neck
                                              </td>
                                              <td
                                                className={`border p-1 ${
                                                  isDarkMode
                                                    ? "border-blue-700"
                                                    : "border-blue-500"
                                                }`}
                                              >
                                                {details.Neck}
                                              </td>
                                              <td
                                                className={`border p-1 ${
                                                  isDarkMode
                                                    ? "border-blue-700"
                                                    : "border-blue-500"
                                                }`}
                                              >
                                                {details.Unit || "cm"}
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  <div
                                    className={`text-xs mt-2 pt-2 border-t ${
                                      isDarkMode
                                        ? "border-blue-800 text-blue-400"
                                        : "border-blue-200 text-blue-600"
                                    }`}
                                  >
                                    <span className="font-medium">
                                      Recorded:
                                    </span>{" "}
                                    {(() => {
                                      const rawDate = Array.isArray(details)
                                        ? details.find(
                                            (item: any) =>
                                              item.name === "recordedAt",
                                          )?.value
                                        : details.recordedAt;

                                      if (!rawDate) {
                                        return `${selectedConsultation?.date || ""} ${
                                          selectedConsultation?.time || ""
                                        }`;
                                      }

                                      return moment(rawDate).format(
                                        "DD/MM/YYYY, hh:mm A",
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Show details if vaccine */}
                            {type === "vaccine" && details.length > 0 && (
                              <div className="mt-2">
                                <div
                                  className={`p-3 rounded-lg border ${
                                    isDarkMode
                                      ? "border-cyan-700 bg-gray-800"
                                      : "border-cyan-300 bg-cyan-50"
                                  }`}
                                >
                                  <h3
                                    className={`text-sm font-bold ${
                                      isDarkMode
                                        ? "text-cyan-400"
                                        : "text-cyan-700"
                                    } border-b border-cyan-500 pb-1 mb-3`}
                                  >
                                    Vaccination Details
                                  </h3>
                                  {details.map(
                                    (vaccine: any, index: number) => (
                                      <div
                                        key={index}
                                        className="mb-3 last:mb-0"
                                      >
                                        <div
                                          className={`text-xs font-semibold mb-1 ${
                                            isDarkMode
                                              ? "text-cyan-300"
                                              : "text-cyan-600"
                                          }`}
                                        >
                                          {vaccine.vaccineName || "Vaccine"}
                                        </div>
                                        <table
                                          className={`w-full text-xs ${
                                            isDarkMode
                                              ? "text-gray-300"
                                              : "text-gray-700"
                                          }`}
                                        >
                                          <tbody>
                                            {vaccine.dose && (
                                              <tr>
                                                <td className="pr-2 py-0.5 font-medium">
                                                  Dose:
                                                </td>
                                                <td className="py-0.5">
                                                  {vaccine.dose}
                                                </td>
                                              </tr>
                                            )}
                                            {vaccine.schedule && (
                                              <tr>
                                                <td className="pr-2 py-0.5 font-medium">
                                                  Schedule:
                                                </td>
                                                <td className="py-0.5">
                                                  {vaccine.schedule}
                                                </td>
                                              </tr>
                                            )}
                                            {vaccine.site && (
                                              <tr>
                                                <td className="pr-2 py-0.5 font-medium">
                                                  Site:
                                                </td>
                                                <td className="py-0.5">
                                                  {vaccine.site}
                                                </td>
                                              </tr>
                                            )}
                                            {vaccine.manufacturer && (
                                              <tr>
                                                <td className="pr-2 py-0.5 font-medium">
                                                  Manufacturer:
                                                </td>
                                                <td className="py-0.5">
                                                  {vaccine.manufacturer}
                                                </td>
                                              </tr>
                                            )}
                                            {vaccine.batchNumber && (
                                              <tr>
                                                <td className="pr-2 py-0.5 font-medium">
                                                  Batch No:
                                                </td>
                                                <td className="py-0.5">
                                                  {vaccine.batchNumber}
                                                </td>
                                              </tr>
                                            )}
                                            {vaccine.expiryDate && (
                                              <tr>
                                                <td className="pr-2 py-0.5 font-medium">
                                                  Expiry Date:
                                                </td>
                                                <td className="py-0.5">
                                                  {vaccine.expiryDate}
                                                </td>
                                              </tr>
                                            )}
                                            {vaccine.nextDueDate && (
                                              <tr>
                                                <td className="pr-2 py-0.5 font-medium">
                                                  Next Due:
                                                </td>
                                                <td className="py-0.5">
                                                  {vaccine.nextDueDate}
                                                </td>
                                              </tr>
                                            )}
                                            {vaccine.note && (
                                              <tr>
                                                <td className="pr-2 py-0.5 font-medium">
                                                  Note:
                                                </td>
                                                <td className="py-0.5">
                                                  {vaccine.note}
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                        {vaccineData?.recordedAt && (
                                          <div
                                            className={`text-xs mt-1 pt-1 border-t ${
                                              isDarkMode
                                                ? "border-cyan-800 text-cyan-400"
                                                : "border-cyan-200 text-cyan-600"
                                            }`}
                                          >
                                            <span className="font-medium">
                                              Administered:
                                            </span>{" "}
                                            {moment(
                                              vaccineData.recordedAt,
                                            ).format("DD/MM/YYYY HH:mm")}
                                          </div>
                                        )}
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Show details if appointment */}
                            {type === "appointment" && details.length > 0 && (
                              <div className="mt-2">
                                <div
                                  className={`p-3 rounded-lg border ${
                                    isDarkMode
                                      ? "border-amber-700 bg-gray-800"
                                      : "border-amber-300 bg-amber-50"
                                  }`}
                                >
                                  <h3
                                    className={`text-xs font-semibold mb-2 ${
                                      isDarkMode
                                        ? "text-amber-300"
                                        : "text-amber-700"
                                    }`}
                                  >
                                    Appointment Details
                                  </h3>
                                  <div
                                    className={`text-xs ${
                                      isDarkMode
                                        ? "text-gray-300"
                                        : "text-gray-700"
                                    }`}
                                  >
                                    <div className="grid grid-cols-1 gap-1">
                                      {details.map(
                                        (appt: any, index: number) => (
                                          <div
                                            key={index}
                                            className="flex items-center gap-2"
                                          >
                                            <Calendar className="flex-shrink-0 w-3 h-3" />
                                            <span className="font-medium">
                                              {appt.label}:
                                            </span>
                                            <span>{appt.value}</span>
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Show details if signature */}
                            {type === "signature" && details.length > 0 && (
                              <div className="mt-2">
                                <div
                                  className={`p-3 rounded-lg border ${
                                    isDarkMode
                                      ? "border-indigo-700 bg-gray-800"
                                      : "border-indigo-300 bg-indigo-50"
                                  }`}
                                >
                                  <h3
                                    className={`text-xs font-semibold mb-2 ${
                                      isDarkMode
                                        ? "text-indigo-300"
                                        : "text-indigo-700"
                                    }`}
                                  >
                                    Consent Details
                                  </h3>
                                  <ul className="space-y-1 text-xs">
                                    {details.map((d, i) => (
                                      <li
                                        key={i}
                                        className={
                                          isDarkMode
                                            ? "text-gray-300"
                                            : "text-gray-700"
                                        }
                                      >
                                        <span className="font-medium">
                                          {d.label}:
                                        </span>{" "}
                                        {d.value}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            )}

                            {/* Show details if system action */}
                            {type === "system" && details.length > 0 && (
                              <ul className="space-y-1 text-xs">
                                {details.map((d, i) => (
                                  <li
                                    key={i}
                                    className={
                                      isDarkMode
                                        ? "text-gray-300"
                                        : "text-gray-700"
                                    }
                                  >
                                    • {d.label}: {d.value}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
              {/* Timeline of Events */}
              <div>
                <ConsultationTimeline
                  isDarkMode={isDarkMode}
                  className="mb-6"
                />
              </div>

              {/* Close Button */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  onClick={() => {
                    if (!hasConsultationPermission) {
                      setSelectedConsultation(null);
                      setShowPermissionMessage(
                        "You don't have permission to start consultation.",
                      );
                      setShowPermissionModal(true);
                      return;
                    }

                    handleArrowClick(
                      selectedConsultation?.patientId,
                      selectedConsultation,
                    );
                    setSelectedConsultation(null);
                  }}
                  className="text-white bg-blue-600 hover:bg-blue-700"
                >
                  Enter Consultation
                </Button>

                <Button
                  onClick={() => setSelectedConsultation(null)}
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ConsultationDetails;
